import { useEffect } from 'react';

/**
 * French for Zudoku's own interface.
 *
 * Zudoku 0.83 has no i18n: a handful of interface strings (« Search », « On
 * this page », the search dialog…) are hard-coded in English. This component,
 * mounted once in the header, swaps exactly those strings for French after the
 * page has hydrated, and watches for the ones that appear later (dialogs,
 * menus). It only ever replaces a text node or attribute whose WHOLE value is a
 * known English string, so page content is never touched.
 *
 * It deliberately does NOT change `<html lang>` (Zudoku's template hard-codes
 * "en", and a `<Head><html lang>` cannot override it at prerender time).
 * Pagefind indexes and searches per page language, so flipping `lang` only in
 * the browser would point the search at an index that does not exist. One
 * consistent value everywhere keeps a single index covering the whole site.
 */

const TEXT: Record<string, string> = {
  Search: 'Rechercher',
  'On this page': 'Sur cette page',
  'Copy page': 'Copier la page',
  'Use in Claude': 'Ouvrir dans Claude',
  'Use in ChatGPT': 'Ouvrir dans ChatGPT',
  'Start typing to search': 'Tapez un mot pour lancer la recherche',
  'No results found.': 'Aucun résultat.',
  'Clear search': 'Effacer la recherche',
  'An error occurred while loading search.': 'La recherche n’a pas pu se charger.',
  'Build Search Index': 'Construire l’index de recherche',
  'Go back home': 'Revenir à l’accueil',
};

/** Words too common to translate anywhere: only inside the search dialog / sidebar back row. */
const DIALOG_TEXT: Record<string, string> = {
  Navigate: 'Parcourir',
  Select: 'Ouvrir',
  'Close dialog': 'Fermer',
};
const NAV_TEXT: Record<string, string> = {
  Back: 'Retour',
};

const ATTRS: Record<string, string> = {
  'Search...': 'Rechercher dans l’aide…',
  'Toggle table of contents': 'Afficher le sommaire',
  'Expand sidebar': 'Afficher le menu',
  'Collapse sidebar': 'Masquer le menu',
  'Toggle theme': 'Changer de thème',
  'Switch to light mode': 'Passer en mode clair',
  'Switch to dark mode': 'Passer en mode sombre',
  'Page actions': 'Actions sur la page',
  'More actions': 'Plus d’actions',
  'Expand section': 'Déplier',
  'Collapse section': 'Replier',
};

const TRANSLATED_ATTRS = ['placeholder', 'aria-label', 'title'] as const;
const SKIP = 'pre, code, textarea, [contenteditable], [data-noe-no-translate]';

/**
 * Has React hydrated this node yet? Zudoku hydrates page content in Suspense
 * chunks after the header, so a node may still be server HTML waiting for
 * React. Editing it then would make React see a mismatch and re-render the
 * whole page. React tags every node it owns with a `__reactFiber$…` property;
 * nodes created only on the client (dialogs, menus) carry it from birth.
 */
const isHydrated = (node: Node) => Object.keys(node).some((k) => k.startsWith('__reactFiber$'));

const translateText = (node: Text) => {
  const raw = node.nodeValue;
  // A string that is an element's only child is set as that element's text
  // content and has no fiber of its own, so its parent tells us instead.
  if (!raw || !(isHydrated(node) || (node.parentElement && isHydrated(node.parentElement)))) return;
  const key = raw.trim();
  let fr = TEXT[key];
  const count = /^(\d+) results? for "(.*)"$/s.exec(key);
  if (count && node.parentElement?.closest('[role="dialog"]')) {
    fr = `${count[1]} résultat${count[1] === '1' ? '' : 's'} pour « ${count[2]} »`;
  }
  if (!fr && (DIALOG_TEXT[key] || NAV_TEXT[key] || key.startsWith('Back to '))) {
    const parent = node.parentElement;
    if (DIALOG_TEXT[key] && parent?.closest('[role="dialog"]')) fr = DIALOG_TEXT[key];
    else if (parent?.closest('nav')) {
      fr = NAV_TEXT[key] ?? (key.startsWith('Back to ') ? `Retour : ${key.slice(8)}` : undefined);
    }
  }
  if (fr && fr !== key) node.nodeValue = raw.replace(key, fr);
};

const translateElement = (el: Element) => {
  if (!TRANSLATED_ATTRS.some((a) => el.hasAttribute(a)) || !isHydrated(el)) return;
  for (const attr of TRANSLATED_ATTRS) {
    const value = el.getAttribute(attr);
    if (value && ATTRS[value]) el.setAttribute(attr, ATTRS[value]);
    else if (value && TEXT[value]) el.setAttribute(attr, TEXT[value]);
  }
};

const translateTree = (root: Node) => {
  if (root.nodeType === Node.TEXT_NODE) {
    translateText(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const el = root as Element;
  // Code samples stay exactly as written.
  if (el.closest(SKIP)) return;
  translateElement(el);
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const parent = current.parentElement;
      if (parent && !parent.closest(SKIP)) translateText(current as Text);
    } else {
      translateElement(current as Element);
    }
    current = walker.nextNode();
  }
};

export const FrenchUi = () => {
  useEffect(() => {
    translateTree(document.body);
    // Content that hydrates after the header produces no DOM mutation (React
    // reuses the server nodes), so sweep again as it comes in, then on idle.
    const sweeps = [400, 1200, 2500, 5000, 9000].map((ms) =>
      window.setTimeout(() => translateTree(document.body), ms),
    );
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData') translateTree(m.target);
        else if (m.type === 'attributes') translateElement(m.target as Element);
        else m.addedNodes.forEach(translateTree);
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TRANSLATED_ATTRS],
    });
    return () => {
      observer.disconnect();
      sweeps.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  return null;
};
